import React, { useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

// Helper to get auth token
const getToken = async (user: any): Promise<string> => {
  const local = localStorage.getItem('accessToken');
  if (local) return local;
  if (user?.getIdToken) return user.getIdToken();
  return '';
};

interface Props {
  replicaId: string;
  replicaName?: string;
  alignmentScore?: number;
  alignmentTier?: string;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const EXAMPLE_PROMPTS = [
  'Reply to this client asking for a discount',
  'Schedule a meeting with the team next week',
  'Send a follow-up email to the investor',
  'Approve a purchase order for €2,000',
  'Share our financial projections with external partner',
];

export const LiveSimulation: React.FC<Props> = ({
  replicaId,
  replicaName = 'Replica',
  alignmentScore = 0,
  alignmentTier = 'guessing',
}) => {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{
    response: string;
    guardianStatus: 'allowed' | 'escalated' | 'blocked';
    guardianReason?: string;
    responseScore: number;
    appliedRules: string[];
  } | null>(null);

  const runSimulation = useCallback(async () => {
    if (!prompt.trim()) return;
    setRunning(true);
    setResult(null);

    try {
      const authToken = await getToken(user);
      // Simulate the response using the chat API with simulation=true
      const res = await fetch(`${API_BASE}/api/v1/cognitive/${replicaId}/simulate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          message: prompt,
        }),
      });
      const data = await res.json();

      if (data.success || data.reply) {
        // Parse guardian signals from the response
        const isBlocked = data.guardianStatus === 'blocked' || data.blocked;
        const isEscalated = data.guardianStatus === 'escalated' || data.escalated;
        setResult({
          response: data.reply || data.message || 'Simulation completed.',
          guardianStatus: isBlocked ? 'blocked' : isEscalated ? 'escalated' : 'allowed',
          guardianReason: data.guardianReason || data.escalationReason,
          responseScore: data.alignmentScore || alignmentScore,
          appliedRules: data.appliedRules || [],
        });
      }
    } catch (e) {
      setResult({
        response: '⚠️ Simulation unavailable — backend not reachable.',
        guardianStatus: 'blocked',
        guardianReason: 'Connection error',
        responseScore: 0,
        appliedRules: [],
      });
    } finally {
      setRunning(false);
    }
  }, [prompt, replicaId, user, alignmentScore]);

  const guardianColor = (status: string) => {
    switch (status) {
      case 'allowed': return { bg: '#16a34a20', border: '#22c55e40', text: '#22c55e', icon: '✅' };
      case 'escalated': return { bg: '#f59e0b20', border: '#f59e0b40', text: '#f59e0b', icon: '⚠️' };
      case 'blocked': return { bg: '#ef444420', border: '#ef444440', text: '#ef4444', icon: '🛑' };
      default: return { bg: '#64748b20', border: '#64748b40', text: '#64748b', icon: '❓' };
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-700/50 bg-slate-800/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-white">Live Simulation</h3>
            <p className="text-xs text-slate-400">Test how {replicaName} would respond</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Alignment:</span>
            <span className="text-xs font-semibold" style={{
              color: alignmentTier === 'high_fidelity' ? '#22c55e' :
                     alignmentTier === 'operational' ? '#6366f1' :
                     alignmentTier === 'limited' ? '#f59e0b' : '#64748b'
            }}>
              {alignmentScore}%
            </span>
          </div>
        </div>
      </div>

      {/* Prompt Input */}
      <div className="p-4 border-b border-slate-700/30">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) runSimulation(); }}
          placeholder="Type a scenario to simulate..."
          className="w-full h-20 bg-slate-800/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-slate-500">Ctrl+Enter to run</span>
          <button
            onClick={runSimulation}
            disabled={running || !prompt.trim()}
            className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-2"
          >
            {running ? (
              <>
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Running...
              </>
            ) : '▶ Run Simulation'}
          </button>
        </div>
      </div>

      {/* Quick prompts */}
      {!result && (
        <div className="px-4 py-3 space-y-1.5">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Quick Scenarios</p>
          {EXAMPLE_PROMPTS.map((p, i) => (
            <button
              key={i}
              onClick={() => setPrompt(p)}
              className="block w-full text-left text-xs text-slate-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-slate-700/30 transition-colors"
            >
              → {p}
            </button>
          ))}
        </div>
      )}

      {/* Simulation Result */}
      {result && (
        <div className="flex-1 p-4 space-y-3 overflow-auto">
          {/* Guardian Status */}
          <div
            className="rounded-xl px-4 py-3 border"
            style={{
              backgroundColor: guardianColor(result.guardianStatus).bg,
              borderColor: guardianColor(result.guardianStatus).border,
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm">{guardianColor(result.guardianStatus).icon}</span>
              <span
                className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: guardianColor(result.guardianStatus).text }}
              >
                Guardian: {result.guardianStatus}
              </span>
            </div>
            {result.guardianReason && (
              <p className="text-xs text-slate-300 mt-1">{result.guardianReason}</p>
            )}
          </div>

          {/* Response */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-indigo-900/50 flex items-center justify-center text-xs">🤖</div>
              <span className="text-xs font-semibold text-slate-300">{replicaName}</span>
            </div>
            <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
              {result.response}
            </p>
          </div>

          {/* Applied Rules */}
          {result.appliedRules.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">Applied Rules</p>
              {result.appliedRules.map((rule, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-400">
                  <span className="text-indigo-400">→</span>
                  <span>{rule}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveSimulation;
